import React, { useState, useEffect } from 'react';
import { getUsers, deleteUser, updateUser, resetUserPassword } from '../services/api';
import { formatDate } from '../utils/formatDate';
import UserForm from './UserForm';
import './UserManagement.css';

function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Bulk selection states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkActionConfirm, setBulkActionConfirm] = useState(null);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      if (response.success) {
        setUsers(response.users);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = !roleFilter || user.role === roleFilter;
    
    const matchesStatus = !statusFilter || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle add new user
  const handleAddNew = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  // Handle edit user
  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  // Handle delete user
  const handleDelete = async (userId) => {
    try {
      setActionLoading(true);
      const response = await deleteUser(userId);
      if (response.success) {
        setDeleteConfirm(null);
        fetchUsers();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingUser(null);
    fetchUsers();
    setSuccessMessage('User saved successfully');
  };

  // Toggle user active status
  const handleToggleStatus = async (user) => {
    try {
      setActionLoading(true);
      const response = await updateUser(user.id, { is_active: !user.is_active });
      if (response.success) {
        fetchUsers();
        setSuccessMessage(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await resetUserPassword(resetPasswordUser.id, newPassword);
      if (response.success) {
        setResetPasswordUser(null);
        setNewPassword('');
        setSuccessMessage('Password reset successfully');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const selectableUsers = filteredUsers.filter(u => u.id !== currentUser?.id);
      setSelectedUsers(selectableUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    try {
      setActionLoading(true);
      
      if (action === 'delete') {
        for (const userId of selectedUsers) {
          await deleteUser(userId);
        }
        setSuccessMessage(`${selectedUsers.length} user(s) deleted successfully`);
      } else if (action === 'activate' || action === 'deactivate') {
        const isActive = action === 'activate';
        for (const userId of selectedUsers) {
          await updateUser(userId, { is_active: isActive });
        }
        setSuccessMessage(`${selectedUsers.length} user(s) ${action}d successfully`);
      }
      
      setSelectedUsers([]);
      setBulkActionConfirm(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Username', 'Full Name', 'Email', 'Role', 'Department', 'Employee ID', 'Status', 'Last Login', 'Created At'];
    const rows = filteredUsers.map(user => [
      user.username,
      user.full_name || '',
      user.email || '',
      user.role,
      user.department || '',
      user.employee_id || '',
      user.is_active ? 'Active' : 'Inactive',
      user.last_login ? new Date(user.last_login).toLocaleString() : '',
      user.created_at ? new Date(user.created_at).toLocaleString() : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Render role badge – matches backend roles (User.ROLE_*)
  const renderRoleBadge = (role) => {
    const roleConfig = {
      super_admin: { label: 'Super Admin', color: '#b60000' },
      sm_tender_management: { label: 'SM Tender Management', color: '#2563eb' },
      sm_product_manager: { label: 'SM Product Manager', color: '#7c3aed' },
      sanbee_employee: { label: 'Sanbee Employee', color: '#059669' },
    };
    const config = roleConfig[role] || { label: role || '—', color: '#6b7280' };
    return (
      <span className="role-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  // Render status badge
  const renderStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <div className="header-title">
          <h2>User Management</h2>
          <p>Manage system users, roles, and permissions</p>
        </div>
        <div className="header-actions">
          <button className="export-btn" onClick={handleExportCSV} title="Export to CSV">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export
          </button>
          <button className="add-user-btn" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            Add User
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="role-filter"
        >
          <option value="">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="sm_tender_management">SM Tender Management</option>
          <option value="sm_product_manager">SM Product Manager</option>
          <option value="sanbee_employee">Sanbee Employee</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="selection-count">
            {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
          </span>
          <div className="bulk-buttons">
            <button 
              className="bulk-btn activate"
              onClick={() => setBulkActionConfirm('activate')}
            >
              Activate
            </button>
            <button 
              className="bulk-btn deactivate"
              onClick={() => setBulkActionConfirm('deactivate')}
            >
              Deactivate
            </button>
            <button 
              className="bulk-btn delete"
              onClick={() => setBulkActionConfirm('delete')}
            >
              Delete
            </button>
            <button 
              className="bulk-btn cancel"
              onClick={() => setSelectedUsers([])}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards – roles aligned with backend (User.ROLE_*) */}
      <div className="user-stats">
        <div className="stat-card">
          <span className="stat-number">{users.length}</span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'super_admin').length}</span>
          <span className="stat-label">Super Admins</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'sm_tender_management').length}</span>
          <span className="stat-label">SM Tender Management</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'sm_product_manager').length}</span>
          <span className="stat-label">SM Product Manager</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'sanbee_employee').length}</span>
          <span className="stat-label">Sanbee Employees</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <h3>No users found</h3>
          <p>
            {searchTerm || roleFilter
              ? 'Try adjusting your filters'
              : 'Get started by adding your first user'}
          </p>
          {!searchTerm && !roleFilter && (
            <button onClick={handleAddNew}>Add User</button>
          )}
        </div>
      ) : (
        /* Users Table */
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input 
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.filter(u => u.id !== currentUser?.id).length}
                  />
                </th>
                <th>User</th>
                <th>Role</th>
                <th>Department</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`${!user.is_active ? 'inactive-row' : ''} ${selectedUsers.includes(user.id) ? 'selected-row' : ''}`}>
                  <td className="checkbox-col">
                    <input 
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      disabled={user.id === currentUser?.id}
                    />
                  </td>
                  <td className="user-info-cell" onClick={() => setViewingUser(user)} style={{ cursor: 'pointer' }}>
                    <div className="user-avatar">
                      {(user.full_name || user.username)?.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-details">
                      <span className="user-name">{user.full_name || user.username}</span>
                      <span className="user-email">{user.email || user.username}</span>
                    </div>
                  </td>
                  <td>{renderRoleBadge(user.role)}</td>
                  <td>{user.department || '-'}</td>
                  <td>
                    <button 
                      className={`status-toggle ${user.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(user)}
                      disabled={user.id === currentUser?.id || actionLoading}
                      title={user.id === currentUser?.id ? "Cannot change your own status" : `Click to ${user.is_active ? 'deactivate' : 'activate'}`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="date-cell">{formatDate(user.last_login)}</td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn view-btn" 
                      onClick={() => setViewingUser(user)}
                      title="View User"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                    <button 
                      className="action-btn edit-btn" 
                      onClick={() => handleEdit(user)}
                      title="Edit User"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button 
                      className="action-btn key-btn" 
                      onClick={() => setResetPasswordUser(user)}
                      title="Reset Password"
                      disabled={user.id === currentUser?.id}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                      </svg>
                    </button>
                    <button 
                      className="action-btn delete-btn" 
                      onClick={() => setDeleteConfirm(user)}
                      title="Delete User"
                      disabled={user.id === currentUser?.id}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Table Footer */}
      {!loading && filteredUsers.length > 0 && (
        <div className="table-footer">
          <span>Showing {filteredUsers.length} of {users.length} users</span>
        </div>
      )}

      {/* User Form Modal */}
      {showForm && (
        <UserForm
          user={editingUser}
          onSuccess={handleFormSuccess}
          onClose={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>Delete User</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.full_name || deleteConfirm.username}</strong>?</p>
            <p className="warning">This action cannot be undone. All user data will be permanently removed.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail View Modal */}
      {viewingUser && (
        <div className="modal-overlay" onClick={() => setViewingUser(null)}>
          <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="close-btn" onClick={() => setViewingUser(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="user-detail-content">
              <div className="user-detail-header">
                <div className="user-avatar large">
                  {(viewingUser.full_name || viewingUser.username)?.charAt(0).toUpperCase()}
                </div>
                <div className="user-detail-info">
                  <h3>{viewingUser.full_name || viewingUser.username}</h3>
                  <span className="user-detail-username">@{viewingUser.username}</span>
                  {renderRoleBadge(viewingUser.role)}
                </div>
              </div>
              
              <div className="user-detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <span>{viewingUser.email || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <span>{viewingUser.phone || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Department</label>
                  <span>{viewingUser.department || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Employee ID</label>
                  <span>{viewingUser.employee_id || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  {renderStatusBadge(viewingUser.is_active)}
                </div>
                <div className="detail-item">
                  <label>Last Login</label>
                  <span>{viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleString() : 'Never'}</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleString() : '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Last Updated</label>
                  <span>{viewingUser.updated_at ? new Date(viewingUser.updated_at).toLocaleString() : '-'}</span>
                </div>
              </div>

              <div className="user-detail-actions">
                <button className="edit-user-btn" onClick={() => { setViewingUser(null); handleEdit(viewingUser); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Edit User
                </button>
                {viewingUser.id !== currentUser?.id && (
                  <button className="reset-password-btn" onClick={() => { setViewingUser(null); setResetPasswordUser(viewingUser); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                    </svg>
                    Reset Password
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {resetPasswordUser && (
        <div className="modal-overlay" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>
          <div className="confirm-modal password-reset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon key">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
              </svg>
            </div>
            <h3>Reset Password</h3>
            <p>Enter a new password for <strong>{resetPasswordUser.full_name || resetPasswordUser.username}</strong></p>
            <div className="password-input-group">
              <input
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={actionLoading}
              />
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-reset-btn" 
                onClick={handleResetPassword}
                disabled={actionLoading || !newPassword || newPassword.length < 6}
              >
                {actionLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {bulkActionConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className={`confirm-icon ${bulkActionConfirm === 'delete' ? 'delete' : 'action'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {bulkActionConfirm === 'delete' ? (
                  <>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </>
                ) : (
                  <>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </>
                )}
              </svg>
            </div>
            <h3>
              {bulkActionConfirm === 'delete' ? 'Delete Users' : 
               bulkActionConfirm === 'activate' ? 'Activate Users' : 'Deactivate Users'}
            </h3>
            <p>
              Are you sure you want to {bulkActionConfirm} <strong>{selectedUsers.length}</strong> user{selectedUsers.length > 1 ? 's' : ''}?
            </p>
            {bulkActionConfirm === 'delete' && (
              <p className="warning">This action cannot be undone. All selected user data will be permanently removed.</p>
            )}
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setBulkActionConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className={`confirm-${bulkActionConfirm === 'delete' ? 'delete' : 'action'}-btn`}
                onClick={() => handleBulkAction(bulkActionConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : `${bulkActionConfirm.charAt(0).toUpperCase() + bulkActionConfirm.slice(1)} Users`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
